import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { RegisterFormData } from '../types/auth';
import { registerSchema } from '../utils/validation';
import { useAuth } from './useAuth';
import { UserRole } from '../types/auth';

export const useRegister = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register: registerUser, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    clearErrors,
    trigger,
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema) as any,
    mode: 'onChange',
    defaultValues: {
      fullName: '',
      furigana: '',
      university: '',
      faculty: '',
      phone: '',
      birthDate: '',
      gender: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    },
  });

  const watchedValues = watch();

  const onSubmit = async (data: RegisterFormData) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    clearError();

    try {
      await registerUser({
        fullName: data.fullName,
        furigana: data.furigana,
        university: data.university,
        faculty: data.faculty,
        phone: data.phone,
        birthDate: data.birthDate,
        gender: data.gender,
        email: data.email,
        password: data.password,
        userRole: UserRole.STUDENT,
      });

      // 登録成功後、メール確認ダイアログを表示
      setRegisteredEmail(data.email);
      setShowSuccessDialog(true);
    } catch (error: any) {
      // Error is handled by the auth context
      console.error('Registration submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldFocus = (fieldName: keyof RegisterFormData) => {
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (authError) {
      clearError();
    }
  };

  const resetForm = () => {
    setValue('fullName', '');
    setValue('furigana', '');
    setValue('university', '');
    setValue('faculty', '');
    setValue('phone', '');
    setValue('birthDate', '');
    setValue('gender', '');
    setValue('email', '');
    setValue('password', '');
    setValue('confirmPassword', '');
    setValue('agreeToTerms', false);
    clearErrors();
    clearError();
  };

  return {
    // Form state
    register,
    handleSubmit: handleSubmit(onSubmit as any),
    errors,
    isValid,
    watchedValues,

    // Submission state
    isSubmitting,
    authError,

    // Success dialog state
    showSuccessDialog,
    setShowSuccessDialog,
    registeredEmail,

    // Form actions
    handleFieldFocus,
    resetForm,
    setValue,
    clearError,
    trigger,
    navigate,

    // Field validation helpers
    hasFieldError: (field: keyof RegisterFormData) => !!errors[field],
    getFieldError: (field: keyof RegisterFormData) => errors[field]?.message,
  };
};

// Hook for managing registration progress
export const useRegistrationProgress = () => {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const markStepCompleted = (step: number) => {
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
  };

  const markStepIncomplete = (step: number) => {
    setCompletedSteps((prev) => prev.filter((s) => s !== step));
  };

  const resetProgress = () => {
    setCompletedSteps([]);
  };

  return {
    completedSteps,
    markStepCompleted,
    markStepIncomplete,
    resetProgress,
    isStepCompleted: (step: number) => completedSteps.includes(step),
    getProgress: () => (completedSteps.length / 3) * 100,
  };
};
